import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";
import { useSelector } from "react-redux";
import { Colors } from "../Colors";
import { RootState, useAppDispatch } from "../store";
import { getSession } from "../slices/AuthSlice";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { FrontendUser } from "../types";

type OverviewAgent = {
  agentId: string;
  title?: string;
  agentType: string;
  isActive: boolean;
};

type OverviewOrganization = {
  organizationId: string;
  name: string;
  agents: OverviewAgent[];
};

type OverviewSuperOrganization = {
  superOrganizationId: string;
  name: string;
  deactivated: boolean;
  organizations: OverviewOrganization[];
};

type StatusFilter = "all" | "active" | "agentsOff" | "deactivated";

const tableContainerSx = {
  backgroundColor: "rgba(255, 255, 255, 0.02)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  borderRadius: "12px",
  boxShadow: "none",
};

const tableHeadRowSx = {
  "& th": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    color: Colors.info,
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    py: 2,
  },
};

const tableBodyRowSx = {
  "& td": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
    py: 1.5,
  },
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  transition: "background-color 0.2s ease",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.03)",
    borderRadius: "8px",
    "& fieldset": { borderColor: "rgba(255, 255, 255, 0.08)" },
    "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.15)" },
    "&.Mui-focused fieldset": { borderColor: Colors.primary },
  },
  "& .MuiOutlinedInput-input": { color: "#FFF" },
};

function statusChipSx(color: string, tint: string) {
  return {
    backgroundColor: `rgba(${tint}, 0.12)`,
    color,
    border: `1px solid rgba(${tint}, 0.3)`,
    fontSize: "0.7rem",
    fontWeight: 600,
    height: "22px",
  };
}

const activeChipSx = statusChipSx(Colors.success, "0, 172, 79");
const inactiveChipSx = statusChipSx(Colors.error, "238, 38, 38");
const warningChipSx = statusChipSx(Colors.warning, "252, 213, 84");
const neutralChipSx = statusChipSx(Colors.info, "159, 161, 166");

function agentsOf(superOrganization: OverviewSuperOrganization) {
  return superOrganization.organizations.flatMap((org) => org.agents);
}

function activeAgentCount(superOrganization: OverviewSuperOrganization) {
  return agentsOf(superOrganization).filter((agent) => agent.isActive).length;
}

function matchesFilter(superOrganization: OverviewSuperOrganization, filter: StatusFilter) {
  switch (filter) {
    case "active":
      return !superOrganization.deactivated;
    case "agentsOff":
      return !superOrganization.deactivated && activeAgentCount(superOrganization) === 0;
    case "deactivated":
      return superOrganization.deactivated;
    default:
      return true;
  }
}

function simplifyString(str: string) {
  return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

function matchesQuery(superOrganization: OverviewSuperOrganization, query: string) {
  if (!query) return true;
  const simpleQuery = simplifyString(query);
  const names = [superOrganization.name, superOrganization.superOrganizationId, ...superOrganization.organizations.map((org) => org.name)];
  return names.some((name) => simplifyString(name || "").includes(simpleQuery));
}

interface SuperOrganizationRowProps {
  superOrganization: OverviewSuperOrganization;
  onToggle: (superOrganization: OverviewSuperOrganization) => void;
}

const SuperOrganizationRow: React.FC<SuperOrganizationRowProps> = ({ superOrganization, onToggle }) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const totalAgents = agentsOf(superOrganization).length;
  const activeAgents = activeAgentCount(superOrganization);
  const agentsRunningWhileDeactivated = superOrganization.deactivated && activeAgents > 0;

  return (
    <>
      <TableRow sx={{ ...tableBodyRowSx, cursor: "pointer" }} onClick={() => setOpen((prev) => !prev)}>
        <TableCell sx={{ pl: 2, width: 48 }}>
          <IconButton size="small" sx={{ color: Colors.info }}>
            {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#FFF" }}>{superOrganization.name || "Unnamed"}</Typography>
          <Typography sx={{ fontSize: "0.7rem", color: Colors.info, mt: 0.25, fontFamily: "monospace" }}>{superOrganization.superOrganizationId}</Typography>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip label={superOrganization.deactivated ? "Deactivated" : "Active"} size="small" sx={superOrganization.deactivated ? inactiveChipSx : activeChipSx} />
            {agentsRunningWhileDeactivated && (
              <Tooltip title="This super organization is deactivated but still has agents turned on. WebPT polling is skipped for deactivated super organizations." arrow>
                <WarningAmberIcon sx={{ color: Colors.warning, fontSize: 18 }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: "0.8rem", color: Colors.info }}>{superOrganization.organizations.length}</Typography>
        </TableCell>
        <TableCell>
          {totalAgents === 0 ? (
            <Chip label="No agents" size="small" sx={neutralChipSx} />
          ) : (
            <Chip label={`${activeAgents} / ${totalAgents} on`} size="small" sx={activeAgents === 0 ? warningChipSx : activeChipSx} />
          )}
        </TableCell>
        <TableCell sx={{ pr: 3, textAlign: "right" }}>
          <Button
            size="small"
            variant="outlined"
            color={superOrganization.deactivated ? "success" : "error"}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(superOrganization);
            }}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
          >
            {superOrganization.deactivated ? "Reactivate" : "Deactivate"}
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? "1px solid rgba(255, 255, 255, 0.06)" : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 9, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {superOrganization.organizations.length === 0 && <Typography sx={{ fontSize: "0.8rem", color: Colors.info }}>No organizations.</Typography>}
              {superOrganization.organizations.map((org) => {
                const orgIsActive = org.agents.some((agent) => agent.isActive);
                return (
                  <Box key={org.organizationId} sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ minWidth: 260, maxWidth: 260 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#FFF" }}>{org.name || "Unnamed"}</Typography>
                        <Chip label={orgIsActive ? "Active" : "Inactive"} size="small" sx={orgIsActive ? activeChipSx : inactiveChipSx} />
                      </Box>
                      <Typography sx={{ fontSize: "0.7rem", color: Colors.info, mt: 0.25, fontFamily: "monospace" }}>{org.organizationId}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                      {org.agents.length === 0 && <Typography sx={{ fontSize: "0.8rem", color: Colors.info }}>No agents</Typography>}
                      {org.agents.map((agent) => (
                        <Chip key={agent.agentId} label={`${agent.title || agent.agentType} · ${agent.isActive ? "On" : "Off"}`} size="small" sx={agent.isActive ? activeChipSx : neutralChipSx} />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SuperAdminPage: React.FC = () => {
  const user: FrontendUser | null = useSelector((state: RootState) => state.auth.user);
  const dispatch = useAppDispatch();
  const token = user?.token || "";

  const [superOrganizations, setSuperOrganizations] = React.useState<OverviewSuperOrganization[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [pageError, setPageError] = React.useState<string>("");
  const [query, setQuery] = React.useState<string>("");
  const [filter, setFilter] = React.useState<StatusFilter>("all");
  const [toggleTarget, setToggleTarget] = React.useState<OverviewSuperOrganization | null>(null);
  const [toggling, setToggling] = React.useState<boolean>(false);
  const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string }>({ open: false, message: "" });

  React.useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        setPageError("");
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/super-admin/overview`, { headers: { Authorization: `Bearer ${token}` } });
        setSuperOrganizations(response.data.superOrganizations);
      } catch (error) {
        console.error("Error fetching super admin overview:", error);
        setPageError("Failed to load super organizations.");
      } finally {
        setLoading(false);
      }
    };

    if (token && user?.isSuperAdmin) {
      fetchOverview();
    }
  }, [token, user?.isSuperAdmin]);

  async function confirmToggle() {
    if (!toggleTarget || !token) return;
    const deactivated = !toggleTarget.deactivated;
    setToggling(true);
    setPageError("");
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/super-organizations/${toggleTarget.superOrganizationId}`,
        { deactivated },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuperOrganizations((prev) => prev.map((so) => (so.superOrganizationId === toggleTarget.superOrganizationId ? { ...so, deactivated } : so)));
      setSnackbar({ open: true, message: `${toggleTarget.name} ${deactivated ? "deactivated" : "reactivated"}` });
      setToggleTarget(null);
      // refresh the session so the organization list picks up the change
      dispatch(getSession());
    } catch (error) {
      console.error("Error updating super organization:", error);
      setPageError(`Failed to ${deactivated ? "deactivate" : "reactivate"} ${toggleTarget.name}. Please try again.`);
    } finally {
      setToggling(false);
    }
  }

  if (!user?.isSuperAdmin) {
    return (
      <Box sx={{ marginTop: { xs: "75px", sm: 0 }, color: "#FFF" }}>
        <SignoutHeader />
        <Alert severity="error">You don't have access to this page.</Alert>
      </Box>
    );
  }

  const counts: Record<StatusFilter, number> = {
    all: superOrganizations.length,
    active: superOrganizations.filter((so) => matchesFilter(so, "active")).length,
    agentsOff: superOrganizations.filter((so) => matchesFilter(so, "agentsOff")).length,
    deactivated: superOrganizations.filter((so) => matchesFilter(so, "deactivated")).length,
  };

  const filterOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "agentsOff", label: "Active, all agents off" },
    { value: "deactivated", label: "Deactivated" },
  ];

  const visibleSuperOrganizations = superOrganizations.filter((so) => matchesFilter(so, filter) && matchesQuery(so, query));

  const toggleDeactivates = !!toggleTarget && !toggleTarget.deactivated;

  return (
    <Box sx={{ marginTop: { xs: "75px", sm: 0 }, color: "#FFF", minHeight: "100vh", pb: 4 }}>
      <SignoutHeader />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5 }}>
          Super Admin
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          Every super organization, its organizations and their agents. Deactivate super organizations that are no longer in use to hide them from organization lists.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {filterOptions.map((option) => (
            <Chip
              key={option.value}
              label={`${option.label} (${counts[option.value]})`}
              onClick={() => setFilter(option.value)}
              variant={filter === option.value ? "filled" : "outlined"}
              color={filter === option.value ? "primary" : "default"}
              sx={{ fontWeight: 600, color: filter === option.value ? "#FFF" : Colors.info, borderColor: "rgba(255, 255, 255, 0.15)" }}
            />
          ))}
        </Box>
        <TextField
          placeholder="Search super orgs or orgs"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          size="small"
          sx={{ ...inputSx, width: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: Colors.info }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {pageError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {pageError}
        </Alert>
      )}

      {loading && superOrganizations.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={tableContainerSx}>
          <Table>
            <TableHead>
              <TableRow sx={tableHeadRowSx}>
                <TableCell sx={{ width: 48 }} />
                <TableCell>Super organization</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Orgs</TableCell>
                <TableCell>Agents</TableCell>
                <TableCell sx={{ textAlign: "right", pr: 3 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleSuperOrganizations.map((superOrganization) => (
                <SuperOrganizationRow key={superOrganization.superOrganizationId} superOrganization={superOrganization} onToggle={setToggleTarget} />
              ))}
              {!loading && visibleSuperOrganizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 6, color: Colors.info, borderBottom: "none" }}>
                    No super organizations match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(toggleTarget)}
        onClose={() => (toggling ? undefined : setToggleTarget(null))}
        PaperProps={{ sx: { backgroundColor: Colors.bg2, color: "#FFF", borderRadius: "12px", border: "1px solid rgba(255, 255, 255, 0.08)" } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{toggleDeactivates ? "Deactivate" : "Reactivate"} Super Organization</DialogTitle>
        <DialogContent sx={{ color: Colors.info }}>
          {toggleDeactivates
            ? `Deactivate ${toggleTarget?.name}? Its organizations will be hidden from organization lists and it will be skipped by the WebPT webhook. Users can still sign in, and you can reactivate it at any time.`
            : `Reactivate ${toggleTarget?.name}? Its organizations will show up in organization lists again and WebPT polling will resume.`}
          {toggleDeactivates && toggleTarget && activeAgentCount(toggleTarget) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {activeAgentCount(toggleTarget)} agent{activeAgentCount(toggleTarget) === 1 ? " is" : "s are"} still turned on in this super organization.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setToggleTarget(null)} disabled={toggling} sx={{ textTransform: "none", color: Colors.info }}>
            Cancel
          </Button>
          <Button
            onClick={confirmToggle}
            variant="contained"
            color={toggleDeactivates ? "error" : "success"}
            disabled={toggling}
            sx={{ textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" } }}
          >
            {toggling ? "Saving..." : toggleDeactivates ? "Deactivate" : "Reactivate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ open: false, message: "" })} message={snackbar.message} />
    </Box>
  );
};

export default SuperAdminPage;
